/*
  Predictive search: the overlay that drops under the header while someone
  types.

  A custom element, not Alpine. `.claude/rules/javascript-standards.md` splits
  the two by job — Alpine for ephemeral UI state, custom elements for data
  fetching — and this fetches. The version this replaces did both at once: it
  was a custom element carrying `x-model`, `x-ref` and `@focus`, so the input's
  value lived in Alpine while the request lived in the element, and neither
  owned the open state.

  Results and No Results are rendered by Shopify from
  `sections/predictive-results.liquid`; this only decides when to ask, what to
  show meanwhile, and where focus goes.
*/
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      /*
        Live's search-script.js rewrites a few shorthand terms in the field as
        they are typed, so "ref" searches for R•E and "bt" for b•t. Whole-field
        matches, ignoring case: live lists each casing by hand, and its one
        uppercase "REF" goes to b•t, which reads as a slip, so it is not copied.
      */
      static typedAliases = new Map([
        ['puer', "pu'er"],
        ['puer green', "pu'er green"],
        ['puer green tea', "pu'er green"],
        ['puer black', "pu'er black"],
        ['puer black tea', "pu'er black"],
        ['bt', 'b•t'],
        ['btf', 'b•t'],
        ['b•tf', 'b•t'],
        ['btox', 'b•t'],
        ['botox', 'b•t'],
        ['vc', 'daily radiance'],
        ['xe', 'X•E'],
        ['xef', 'X•E'],
        ['x•ef', 'X•E'],
        ['ref', 'R•E'],
      ]);

      /* And these only when the field is submitted, as live's search form does. */
      static submitAliases = new Map([
        ['puer', "pu'er"],
        ['puer black', "pu'erh-black"],
        ['puer black tea', "pu'erh-black"],
        ["pu'er black", "pu'erh-black"],
        ['puer green', "pu'erh-green"],
        ['puer green tea', "pu'erh-green"],
        ["pu'er green", "pu'erh-green"],
        ['btox', 'B•T'],
        ['beauty', 'B•T'],
        ['electrolyte', 'B•T'],
        ['electrolytes', 'B•T'],
        ['fountain', 'B•T'],
        ['hydrate', 'B•T'],
        ['holi', 'Holi Hoji'],
        ['hoji', 'Holi Hoji'],
      ]);

      static aliasFor(value, aliases) {
        return aliases.get(value.toLowerCase());
      }

      constructor() {
        super();
        this.abortController = null;
        this.debounceTimer = null;
        this.isOpen = false;
        this.isClosing = false;

        this.onInput = this.onInput.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onBarToggle = this.onBarToggle.bind(this);
        this.onLoadingTransitionEnd = this.onLoadingTransitionEnd.bind(this);
      }

      connectedCallback() {

        /*
          Two fields, one panel: the desktop header field and the mobile bar
          are both inside this element, and only one is visible at a time. They
          share the overlay rather than each rendering their own, which would
          fetch every trending image twice.
        */
        this.inputs = Array.from(this.querySelectorAll('input[type="search"]'));
        this.input = this.inputs[0];
        this.overlay = this.querySelector('[data-predictive-search]');
        this.output = this.querySelector('[data-predictive-output]');
        this.idle = this.querySelector('[data-predictive-idle]');
        this.status = this.querySelector('[data-predictive-status]');
        this.closeButton = this.querySelector('[data-predictive-close]');
        this.loadingBar = this.querySelector('[data-predictive-loading]');

        /*
          The mobile bar's visibility lives here rather than in Alpine. It was
          an inline `@click` flipping `hidden` on an `x-ref`, which left the
          panel open when the bar was dismissed — a shopper on a phone got a
          full-height sheet of results with no field above it. Same
          split-ownership failure the class comment describes, one level up.
        */
        this.bar = this.querySelector('[data-mobile-search-bar]');
        this.barToggle = this.querySelector('[data-mobile-search-toggle]');
        if (!this.input || !this.overlay) return;

        this.minLength = Number(this.dataset.minLength) || 2;
        this.delay = Number(this.dataset.delay) || 300;

        this.inputs.forEach((input) => {
          input.addEventListener('input', this.onInput);
          input.addEventListener('focus', this.onFocus);
        });
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('submit', this.onSubmit);
        this.closeButton?.addEventListener('click', this.onCloseClick);
        this.barToggle?.addEventListener('click', this.onBarToggle);
        this.loadingBar?.addEventListener('transitionend', this.onLoadingTransitionEnd);
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        window.clearTimeout(this.debounceTimer);
        this.inputs?.forEach((input) => {
          input.removeEventListener('input', this.onInput);
          input.removeEventListener('focus', this.onFocus);
        });
        this.removeEventListener('keydown', this.onKeydown);
        this.removeEventListener('submit', this.onSubmit);
        this.closeButton?.removeEventListener('click', this.onCloseClick);
        this.barToggle?.removeEventListener('click', this.onBarToggle);
        this.loadingBar?.removeEventListener('transitionend', this.onLoadingTransitionEnd);
        document.removeEventListener('click', this.onDocumentClick);
      }

      get term() {
        return this.input.value.trim();
      }

      setActiveInput(input) {
        if (input && this.inputs.includes(input)) this.input = input;
      }

      onFocus(event) {
        this.setActiveInput(event.target);

        /*
          close() returning focus to the input fires this same event. Without
          this guard, closing on an empty term reopened the panel it had just
          hidden: the close button sits outside the input, so refocusing it
          is a real focus transition, not a no-op like Escape's (which never
          left the input in the first place).
        */
        if (this.isClosing) return;

        /* Focused Empty: there is nothing to fetch, so just reveal it. */
        if (this.term.length < this.minLength && this.idle) this.open();
      }

      onInput(event) {
        this.setActiveInput(event.target);
        if (!event.isComposing) this.applyAlias(this.input, PredictiveSearch.typedAliases);
        window.clearTimeout(this.debounceTimer);
        if (this.term.length < this.minLength) {

          /*
            Clearing the timer only stops a request that has not gone out yet.
            One already in flight still resolves and writes its results into a
            panel the shopper has just emptied, so it has to be aborted here as
            well as at the start of search().
          */
          this.abortController?.abort();
          this.output.innerHTML = '';
          this.setIdleVisible(true);
          if (this.idle) this.open();
          else this.close();
          return;
        }
        this.debounceTimer = window.setTimeout(() => this.search(this.term), this.delay);
      }

      /* The forms post natively to /search; this only swaps the term first. */
      onSubmit(event) {
        const field = event.target.querySelector?.('input[name="q"]');
        if (!field) return;
        const alias = PredictiveSearch.aliasFor(field.value.trim(), PredictiveSearch.submitAliases);
        if (alias) field.value = alias;
      }

      applyAlias(input, aliases) {
        const alias = PredictiveSearch.aliasFor(input.value, aliases);
        if (alias) input.value = alias;
      }

      /*
        Full search returns up to twenty products; the header's "Suggestions
        shown" setting decides how many the panel lists, and View all has the
        rest.
      */
      trimResults(panel) {
        const limit = Number(this.dataset.limit) || 8;
        const items = Array.from(panel?.querySelectorAll('.predictive-search__item') || []);
        for (const item of items.slice(limit)) item.remove();
      }

      async search(term) {

        /* One request in flight: a fast typist would otherwise race their own
           earlier queries and land on whichever returned last. */
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;

        /*
          Full search with the term as a prefix, the request live's header
          search makes (`/search?view=ajax&q=<term>*&type=product`), so the
          results and their order are live's. Predictive search
          (`/search/suggest`) used to go first, with this only on a miss: it
          indexes "R•E" so that "re" never matches it yet still returns other
          products, so typing RE never listed R•E Fountain.
        */
        const url = new URL(`${window.Shopify?.routes?.root || '/'}search`, window.location.origin);
        url.searchParams.set('q', `${term}*`);
        url.searchParams.set('type', 'product');
        url.searchParams.set('section_id', 'predictive-results');

        this.setLoading(true);
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
          const panel = this.parsePanel(await response.text());
          this.trimResults(panel);

          this.output.innerHTML = panel ? panel.outerHTML : '';
          this.setIdleVisible(false);
          this.open();
          this.announce();
        } catch (error) {
          if (error.name === 'AbortError') return;

          /* A failed search must not strand an open, empty panel. */
          this.output.innerHTML = '';
          this.setIdleVisible(true);
          console.error(error);
        } finally {

          /*
            Only the latest request ends the loading state. A query superseded
            by the next keystroke settles after its replacement has started,
            and ending it then hid the bar while the new request was still out.
          */
          if (this.abortController === controller) this.setLoading(false);
        }
      }

      setLoading(loading) {
        if (loading) {
          this.setAttribute('data-loading', '');
          this.loadingBar?.classList.remove('is-finished');
          this.loadingBar?.classList.add('is-visible');
          return;
        }
        if (!this.hasAttribute('data-loading')) return;
        this.removeAttribute('data-loading');
        this.loadingBar?.classList.add('is-finished');
      }

      /* inline-size is the bar's only transitioned property, so any end is its. */
      onLoadingTransitionEnd() {
        if (!this.loadingBar.classList.contains('is-finished')) return;
        this.loadingBar.classList.remove('is-visible', 'is-finished');
      }

      parsePanel(text) {
        return new DOMParser().parseFromString(text, 'text/html').querySelector('[data-predictive-panel]');
      }

      announce() {
        if (!this.status) return;
        const count = this.output.querySelectorAll('.predictive-search__item').length;
        this.status.textContent = count
          ? this.dataset.resultsMessage?.replace('{{ count }}', count) || ''
          : this.dataset.emptyMessage || '';
      }

      setIdleVisible(visible) {
        if (this.idle) this.idle.hidden = !visible;
      }

      open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.overlay.hidden = false;
        this.inputs.forEach((i) => i.setAttribute('aria-expanded', 'true'));
      }

      close(returnFocus = false) {
        /*
          Cancel pending work before anything else, and do it even when the
          panel is already closed — otherwise a request still in flight resolves
          into an open() and reopens the panel seconds after the shopper
          dismissed it. Measured on the mobile bar: tapping the magnifier while
          a query was loading hid the bar, then the response arrived and put a
          full-height panel back with no field above it.

          Every dismissal routes through here — Escape, the close button, an
          outside click, the bar toggle — so this is the one place that has to
          know about it.
        */
        this.abortController?.abort();
        window.clearTimeout(this.debounceTimer);
        this.setLoading(false);

        if (!this.isOpen) return;
        this.isOpen = false;
        this.overlay.hidden = true;
        this.inputs.forEach((i) => i.setAttribute('aria-expanded', 'false'));
        if (returnFocus) {
          this.isClosing = true;
          this.input.focus();
          this.isClosing = false;
        }
      }

      onKeydown(event) {
        if (event.key !== 'Escape') return;

        /* Escape hands focus back to the field that opened the panel rather
           than dropping it on the body. */
        this.close(true);
      }

      onCloseClick() {

        /* The close button lives inside the overlay, not the mobile bar, so
           closing the panel alone would leave the bar open behind it. */
        if (this.bar) {
          this.bar.hidden = true;
          this.barToggle?.setAttribute('aria-expanded', 'false');
        }
        
        this.close(true);
      }

      onDocumentClick(event) {
        if (!this.isOpen) return;

        /*
          Not `this.contains()`: this element wraps the entire header, so the
          nav, the cart and the account icon all counted as "inside" and left
          the panel open behind a mega menu. Only the panel itself and the
          fields that drive it should hold it open.
        */
        const inside =
          this.overlay.contains(event.target) ||
          this.inputs.some((input) => input.contains(event.target)) ||
          this.barToggle?.contains(event.target);
        if (inside) return;
        this.close();
      }

      onBarToggle() {
        if (!this.bar) return;
        const willOpen = this.bar.hidden;
        this.bar.hidden = !willOpen;
        this.barToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) {
          const field = this.bar.querySelector('input[type="search"]');
          field?.focus();
          return;
        }

        /* Dismissing the bar dismisses what it opened. */
        this.close();
      }
    },
  );
}
